import { Add } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";

import {
  FindAllTicketsQueryFilters,
  FindAllTicketsQueryFiltersSortByEnum,
  FindAllTicketsQueryFiltersSortOrderEnum,
  FindAllTicketsQueryFiltersTicketStatusEnum,
  FindAllTicketsQueryFiltersTicketTypeEnum,
  TicketDto,
} from "../../api-client";
import useStore from "../../helpers/useStore";
import { UserContext } from "../../stores/User/User.provider";
import { mobiliTheme } from "../../themes/mobiliTheme";
import { useTicketFilter, useTickets } from "../../stores/Ticketing";
import { useTicketFiltersFlateState } from "../Ticketing/hooks/useTicketFilterFlatState";
import TicketDialog from "../Ticketing/TicketDialog";
import TicketIcon from "../Ticketing/TicketEditCreate/TicketIcon";
import TicketFilters from "../Ticketing/TicketFilters";
import CreateTicketForm from "../Ticketing/TicketEditCreate/createTicketForm";
import RxOrdersTable from "./RxOrdersTable";
import BsnrTableToolbar from "../Ticketing/BsnrTableToolbar";

const RxOrdersOverview: React.FC = observer(() => {
  const [refresh, setRefresh] = useState(false);

  const UserStore = useStore(UserContext);
  const { user } = UserStore;

  const [openDialog, setOpenDialog] = useState(false);
  const [openTicket, setOpenTicket] = useState(false);

  const commonFilter: FindAllTicketsQueryFilters = {
    limit: 10,
    locationId: user?.locationId,
    ticketType: [FindAllTicketsQueryFiltersTicketTypeEnum.OrderRx], // Filter for ORDER_RX only
  };

  const defaultFilter: FindAllTicketsQueryFilters = {
    ...commonFilter,
    sortBy: FindAllTicketsQueryFiltersSortByEnum.CreatedAt,
    sortOrder: FindAllTicketsQueryFiltersSortOrderEnum.Asc,
    ticketStatus: [FindAllTicketsQueryFiltersTicketStatusEnum.New],
  };
  const { data: rawTicketFilters, isLoading: ticketFiltersLoading } = useTicketFilter();
  // Get the ORDER_RX ticket filter status
  const orderRxFilter = rawTicketFilters?.find(
    (filter) => filter.ticketType.value === FindAllTicketsQueryFiltersTicketTypeEnum.OrderRx
  );

  // Extract statuses
  const orderRxStatuses =
    orderRxFilter?.statuses?.filter(
      (status) => status.value !== FindAllTicketsQueryFiltersTicketStatusEnum.Closed
    ) ?? [];

  const [ticketFilters, setTicketFilters] = useState<FindAllTicketsQueryFilters>(defaultFilter);

  // Initialize the ticket filters hook
  const {
    filterState,
    showFilters,
    updateType,
    updateStatus,
    updateFromDate,
    updateToDate,
    updateSort,
    setSort,
    resetFilters,
    toggleFilters,
    translatedFlatTicketFilters,
  } = useTicketFiltersFlateState({
    baseFilter: defaultFilter,
    onFiltersChange: (filters) => {
      // Merge the live filter state into the CURRENT filters so the selected tab
      // (ticketStatus) is preserved. Using `filters` (the fresh state) instead of
      // the stale `filterState` closure ensures the sort-direction toggle applies.
      setTicketFilters((prev) => ({
        ...prev,
        ...processFilterState(filters, prev),
      }));
    },
  });

  const processFilterState = (
    state: typeof filterState,
    ticketFilters: FindAllTicketsQueryFilters
  ) => {
    const newFilters = { ...ticketFilters };
    // Always maintain ORDER_RX ticket type
    newFilters.ticketType = [FindAllTicketsQueryFiltersTicketTypeEnum.OrderRx];

    if (state.status && state.status.length > 0) {
      newFilters.ticketStatus = state.status as any;
    }

    if (state.fromDate) {
      newFilters.dateFrom = state.fromDate.toISOString();
    }

    if (state.toDate) {
      newFilters.dateTo = state.toDate.toISOString();
    }

    if (state.sortBy === "priority") {
      newFilters.sortBy = "priority";
      newFilters.sortOrder = state.sortOrder;
    } else if (state.sortBy === "BSNR") {
      // Sort/group by clinic id (backend maps the "BSNR" sort key to rx.clinicId).
      newFilters.sortBy = "BSNR";
      newFilters.sortOrder = state.sortOrder;
    } else {
      newFilters.sortBy = "createdAt";
      newFilters.sortOrder = state.sortOrder;
    }

    return newFilters;
  };

  const [selectedTicketId, setSelectedTicketId] = useState<number | undefined>();
  // Map each status to a tab
  const tabs = orderRxStatuses.map((status) => ({
    label: status.label,
    value: status.value,
  }));

  // Track selected tab
  const [selectedTab, setSelectedTab] = useState(0);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);

    setTicketFilters((prev) => ({
      ...prev,
      ticketStatus: [tabs[newValue].value as FindAllTicketsQueryFiltersTicketStatusEnum],
      ticketType: [FindAllTicketsQueryFiltersTicketTypeEnum.OrderRx], // always ORDER_RX
    }));
  };

  const handleRowClick = async (ticket: any) => {
    try {
      setOpenDialog(true);
      setSelectedTicketId(ticket.id);
    } catch (error) {
      console.error("Error fetching ticket:", error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTicketId(undefined);
  };

  // --- BSNR (clinic) filter ---
  const [bsnrFilter, setBsnrFilter] = useState("");
  const [debouncedBsnrFilter, setDebouncedBsnrFilter] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedBsnrFilter(bsnrFilter.trim()), 350);
    return () => clearTimeout(handle);
  }, [bsnrFilter]);

  const effectiveFilters = useMemo<FindAllTicketsQueryFilters>(
    () => ({
      ...ticketFilters,
      // When the clinic filter is active, results are always grouped/sorted by
      // clinic id (backend maps the "BSNR" sort key to rx.clinicId).
      ...(debouncedBsnrFilter
        ? { BSNR: debouncedBsnrFilter, sortBy: FindAllTicketsQueryFiltersSortByEnum.Bsnr }
        : {}),
    }),
    [ticketFilters, debouncedBsnrFilter]
  );

  const {
    data: ticketsData,
    isFetchingNextPage: isLoading,
    hasNextPage: hasMore,
    fetchNextPage,
  } = useTickets({
    filters: effectiveFilters,
    enabled: !!user?.locationId && !!ticketFilters.locationId,
  });

  useEffect(() => {
    if (user?.locationId) {
      setTicketFilters((prevFilters) => ({
        ...prevFilters,
        locationId: user?.locationId,
      }));
    }
  }, [user?.locationId]);

  const tickets = ticketsData?.pages.reduce((tickets, page) => tickets.concat(page), []);

  return (
    <>
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "440px",
          maxHeight: "440px",
          position: "relative",
        }}
      >
        <CardHeader avatar={<TicketIcon />} title="Rezept Bestellungen" />

        {/* Floating Add Button */}
        <Box
          sx={{
            position: "absolute",
            top: 3,
            right: 16,
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={() => setOpenTicket(true)}
            sx={{ backgroundColor: mobiliTheme.palette.secondary.main }}
          >
            <Add sx={{ color: "white" }} />
          </IconButton>
        </Box>

        {/* Floating Filters - Commented out for now */}
        {/* <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 10,
          }}
        >
          <TicketFilters
            filterState={filterState}
            showFilters={showFilters}
            onTypeChange={updateType}
            onStatusChange={updateStatus}
            onFromDateChange={updateFromDate}
            onToDateChange={updateToDate}
            onSortChange={updateSort}
            onResetFilters={resetFilters}
            onToggleFilters={toggleFilters}
            translatedFlatFilters={translatedFlatTicketFilters}
          />
        </Box> */}

        <CardContent
          sx={{
            height: "400px",
            position: "relative",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            p: 0,
            m: 0,
          }}
        >
          {/* Same tab-strip recipe as the Tickets card: slight breathing room
              above the tabs, afforded by compact tab-label padding (36px strip
              instead of MUI's 48px default). */}
          <Box sx={{ pt: 0.75, px: 1, borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 36,
                "& .MuiTab-root": { minHeight: 36, py: 0.5 },
                // Narrow arrows: the card is only as wide as a dashboard column,
                // so the default 40px scroll buttons would eat a whole tab label.
                "& .MuiTabs-scrollButtons": { width: 20 },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab key={tab.value} label={tab.label} />
              ))}
            </Tabs>
          </Box>

          <BsnrTableToolbar
            bsnrFilter={bsnrFilter}
            onBsnrFilterChange={setBsnrFilter}
            onToggleClinicSort={(active) =>
              active
                ? setSort(
                    FindAllTicketsQueryFiltersSortByEnum.Bsnr,
                    FindAllTicketsQueryFiltersSortOrderEnum.Asc
                  )
                : setSort(
                    defaultFilter.sortBy ?? FindAllTicketsQueryFiltersSortByEnum.CreatedAt,
                    defaultFilter.sortOrder ?? FindAllTicketsQueryFiltersSortOrderEnum.Asc
                  )
            }
            clinicSortActive={filterState.sortBy === FindAllTicketsQueryFiltersSortByEnum.Bsnr}
          />

          {!user || !tickets ? (
            <>
              <CircularProgress sx={{ alignSelf: "center", mt: 4 }} />
            </>
          ) : (
            <>
              {tickets && tickets.length ? (
                <RxOrdersTable
                  handleRowClick={handleRowClick}
                  ticketsData={tickets}
                  hasMoreData={hasMore}
                  fetchMoreData={fetchNextPage}
                  isLoading={isLoading}
                  groupByClinic={effectiveFilters.sortBy === FindAllTicketsQueryFiltersSortByEnum.Bsnr}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <Typography color="text.secondary">Keine Rezept Bestellungen gefunden</Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <CreateTicketForm
        openTicket={openTicket}
        onCloseTicketForm={() => setOpenTicket(!openTicket)}
        refresh={refresh}
        setRefresh={setRefresh}
      />
      <TicketDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        ticketId={selectedTicketId}
        refresh={refresh}
        setRefresh={setRefresh}
        onTicketUpdate={(ticket: TicketDto) => {
          // Handle ticket update if needed
        }}
      />
    </>
  );
});

export default RxOrdersOverview;
